import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiSave } from 'react-icons/fi';

const ProjectInformation = ({ data, updateData, onNext }) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [saveNotification, setSaveNotification] = useState(false);

  const handleInputChange = (field, value) => {
    updateData({ [field]: value });
    
    // Clear validation error when field is updated
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    // Auto-generate project name
    if (field === 'owner' || field === 'location' || field === 'occupancy') {
      generateProjectName();
    }
  };

  const generateProjectName = () => {
    const owner = data.owner || '';
    const location = data.location || '';
    const occupancy = data.occupancy || '';
    
    if (owner && location && occupancy) {
      const ownerSurname = owner.split(' ').pop();
      const cityName = location.split(',')[0] || location;
      const occupancyType = occupancy.split('–')[1]?.trim() || occupancy;
      
      const generatedName = `${ownerSurname} ${cityName} ${occupancyType}`;
      updateData({ projectName: generatedName });
    }
  };

  const validateAndContinue = () => {
    const requiredFields = [
      'projectName', 'owner', 'leadProfession', 'leadName', 'occupancy', 
      'startDate', 'location', 'contact', 'email', 'mobile'
    ];
    
    const errors = {};
    requiredFields.forEach(field => {
      if (!data[field]) {
        errors[field] = true;
      }
    });

    // Validate email format
    if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setValidationErrors(errors);
    
    if (Object.keys(errors).length === 0) {
      onNext();
    } else {
      alert('Please fill out all mandatory fields correctly.');
    }
  };

  const savePartialData = () => {
    setSaveNotification(true);
    setTimeout(() => setSaveNotification(false), 2000);
  };

  const pinLocation = () => {
    if (!data.location) {
      alert('Please enter a project location first.');
      return;
    }
    const encodedLocation = encodeURIComponent(data.location);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
    window.open(googleMapsUrl, '_blank');
  };

  // Location data structure
  const locationData = {
    'Luzon': {
      'National Capital Region (NCR)': {
        'Metro Manila': ['Caloocan City', 'Las Piñas City', 'Makati City', 'Malabon City', 'Mandaluyong City', 'Manila', 'Marikina City', 'Muntinlupa City', 'Navotas City', 'Parañaque City', 'Pasay City', 'Pasig City', 'Quezon City', 'San Juan City', 'Taguig City', 'Valenzuela City']
      },
      'Region I (Ilocos Region)': {
        'Ilocos Norte': ['Laoag City', 'Batac City'],
        'Ilocos Sur': ['Vigan City', 'Candon City'],
        'La Union': ['San Fernando City'],
        'Pangasinan': ['Lingayen', 'Dagupan City', 'San Carlos City', 'Urdaneta City']
      },
      'Region II (Cagayan Valley)': {
        'Batanes': ['Basco'],
        'Cagayan': ['Tuguegarao City'],
        'Isabela': ['Ilagan City', 'Santiago City'],
        'Nueva Vizcaya': ['Bayombong'],
        'Quirino': ['Cabarroguis']
      },
      'Region III (Central Luzon)': {
        'Aurora': ['Baler'],
        'Bataan': ['Balanga City'],
        'Bulacan': ['Malolos City', 'Meycauayan City', 'San Jose del Monte City'],
        'Nueva Ecija': ['Palayan City', 'Cabanatuan City', 'Gapan City', 'San Antonio', 'San Jose City'],
        'Pampanga': ['San Fernando City', 'Angeles City'],
        'Tarlac': ['Tarlac City'],
        'Zambales': ['Iba', 'Olongapo City']
      }
    },
    'Visayas': {
      'Region VI (Western Visayas)': {
        'Aklan': ['Kalibo'],
        'Antique': ['San Jose de Buenavista'],
        'Capiz': ['Roxas City'],
        'Guimaras': ['Jordan'],
        'Iloilo': ['Iloilo City'],
        'Negros Occidental': ['Bacolod City']
      },
      'Region VII (Central Visayas)': {
        'Bohol': ['Tagbilaran City'],
        'Cebu': ['Cebu City', 'Lapu-Lapu City', 'Mandaue City'],
        'Negros Oriental': ['Dumaguete City'],
        'Siquijor': ['Siquijor']
      },
      'Region VIII (Eastern Visayas)': {
        'Biliran': ['Naval'],
        'Eastern Samar': ['Borongan City'],
        'Leyte': ['Tacloban City'],
        'Northern Samar': ['Catarman'],
        'Samar': ['Catbalogan City'],
        'Southern Leyte': ['Maasin City']
      }
    },
    'Mindanao': {
      'Region IX (Zamboanga Peninsula)': {
        'Zamboanga del Norte': ['Dipolog City'],
        'Zamboanga del Sur': ['Pagadian City'],
        'Zamboanga Sibugay': ['Ipil']
      },
      'Region X (Northern Mindanao)': {
        'Bukidnon': ['Malaybalay City'],
        'Camiguin': ['Mambajao'],
        'Lanao del Norte': ['Tubod'],
        'Misamis Occidental': ['Oroquieta City'],
        'Misamis Oriental': ['Cagayan de Oro City']
      },
      'Region XI (Davao Region)': {
        'Compostela Valley': ['Nabunturan'],
        'Davao del Norte': ['Tagum City'],
        'Davao del Sur': ['Davao City'],
        'Davao Occidental': ['Malita'],
        'Davao Oriental': ['Mati City']
      },
      'Region XII (SOCCSKSARGEN)': {
        'North Cotabato': ['Kidapawan City'],
        'Sarangani': ['Alabel'],
        'South Cotabato': ['Koronadal City'],
        'Sultan Kudarat': ['Isulan']
      }
    }
  };

  return (
    <section className="card grid grid-cols-1 md:grid-cols-2 gap-4">
      <h2 className="md:col-span-2 text-base font-semibold">Part I — Project Information</h2>
      
      <label className="grid gap-1">
        <span className="label">Project Name <span style={{color: '#ef4444'}}>*</span></span>
        <input 
          className={`input ${validationErrors.projectName ? 'border-red-500' : ''}`}
          value={data.projectName}
          onChange={(e) => handleInputChange('projectName', e.target.value)}
          placeholder="Auto-generated or enter custom name"
          required 
        />
      </label>

      <label className="grid gap-1">
        <span className="label">Owner <span style={{color: '#ef4444'}}>*</span></span>
        <input 
          className={`input ${validationErrors.owner ? 'border-red-500' : ''}`}
          value={data.owner}
          onChange={(e) => handleInputChange('owner', e.target.value)}
          placeholder="Full Name"
          required 
        />
      </label>

      <label className="grid gap-1">
        <span className="label">Project Lead (Profession/License) <span style={{color: '#ef4444'}}>*</span></span>
        <select 
          className={`input ${validationErrors.leadProfession ? 'border-red-500' : ''}`}
          value={data.leadProfession}
          onChange={(e) => handleInputChange('leadProfession', e.target.value)}
          required
        >
          <option value="">Select profession</option>
          <option>Construction Project Manager</option>
          <option>Architect</option>
          <option>Engineer</option>
          <option>Builder</option>
        </select>
      </label>

      <label className="grid gap-1">
        <span className="label">Project Lead Name & License No. <span style={{color: '#ef4444'}}>*</span></span>
        <input 
          className={`input ${validationErrors.leadName ? 'border-red-500' : ''}`}
          value={data.leadName}
          onChange={(e) => handleInputChange('leadName', e.target.value)}
          placeholder="Full Name, License No."
          required 
        />
      </label>

      <label className="grid gap-1">
        <span className="label">Project Occupancy <span style={{color: '#ef4444'}}>*</span></span>
        <select 
          className={`input ${validationErrors.occupancy ? 'border-red-500' : ''}`}
          value={data.occupancy}
          onChange={(e) => handleInputChange('occupancy', e.target.value)}
          required
        >
          <option value="">Select occupancy type</option>
          <option>Group A – Residential Dwellings</option>
          <option>Group B – Residential (Hotels and Apartments)</option>
          <option>Group C – Education and Recreation</option>
          <option>Group D – Institutional</option>
          <option>Group E – Business and Mercantile</option>
          <option>Group F – Industrial</option>
          <option>Group G – Storage and Hazardous</option>
          <option>Group H – Assembly Other Than Group I</option>
          <option>Group I – Assembly Occupant Load 1000 or More</option>
          <option>Group J – Accessory</option>
          <option>Group K – Miscellaneous</option>
        </select>
      </label>

      <label className="grid gap-1">
        <span className="label">Expected Start Date <span style={{color: '#ef4444'}}>*</span></span>
        <input 
          className={`input ${validationErrors.startDate ? 'border-red-500' : ''}`}
          type="date"
          value={data.startDate}
          onChange={(e) => handleInputChange('startDate', e.target.value)}
          required 
        />
      </label>

      <div className="md:col-span-2 grid gap-4">
        <span className="label">Project Location <span style={{color: '#ef4444'}}>*</span></span>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="grid gap-1">
            <span className="text-xs text-gray-600">Island Group</span>
            <select 
              className="input"
              value={data.islandGroup || ''}
              onChange={(e) => {
                updateData({ 
                  islandGroup: e.target.value, 
                  region: '', 
                  province: '', 
                  city: '',
                  location: ''
                });
              }}
            >
              <option value="">Select Island Group</option>
              {Object.keys(locationData).map(island => (
                <option key={island} value={island}>{island}</option>
              ))}
            </select>
          </label>

          <label className="grid gap-1">
            <span className="text-xs text-gray-600">Region</span>
            <select 
              className={`input ${!data.islandGroup ? 'disabled' : ''}`}
              disabled={!data.islandGroup}
              value={data.region || ''}
              onChange={(e) => {
                updateData({ 
                  region: e.target.value, 
                  province: '', 
                  city: '',
                  location: ''
                });
              }}
            >
              <option value="">Select Region</option>
              {data.islandGroup && Object.keys(locationData[data.islandGroup]).map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </label>

          <label className="grid gap-1">
            <span className="text-xs text-gray-600">Province</span>
            <select 
              className={`input ${!data.region ? 'disabled' : ''}`}
              disabled={!data.region}
              value={data.province || ''}
              onChange={(e) => {
                updateData({ 
                  province: e.target.value, 
                  city: '',
                  location: ''
                });
              }}
            >
              <option value="">Select Province</option>
              {data.region && Object.keys(locationData[data.islandGroup][data.region]).map(province => (
                <option key={province} value={province}>{province}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="grid gap-1">
            <span className="text-xs text-gray-600">City/Municipality</span>
            <select 
              className={`input ${!data.province ? 'disabled' : ''}`}
              disabled={!data.province}
              value={data.city || ''}
              onChange={(e) => {
                updateData({ city: e.target.value });
                // Auto-generate location string
                if (e.target.value) {
                  const fullLocation = `${e.target.value}, ${data.province}`;
                  updateData({ location: fullLocation });
                }
              }}
            >
              <option value="">Select City/Municipality</option>
              {data.province && locationData[data.islandGroup]?.[data.region]?.[data.province]?.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </label>

          <div className="grid gap-1">
            <span className="text-xs text-gray-600">Full Address</span>
            <div className="flex gap-2">
              <input 
                className={`input flex-1 ${validationErrors.location ? 'border-red-500' : ''}`}
                value={data.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="Street Address, Barangay"
                required 
              />
              <button type="button" className="btn" onClick={pinLocation}>📍 Pin</button>
            </div>
          </div>
        </div>
      </div>

      <label className="grid gap-1">
        <span className="label">Project Contact Person <span style={{color: '#ef4444'}}>*</span></span>
        <input 
          className={`input ${validationErrors.contact ? 'border-red-500' : ''}`}
          placeholder="First Name M.I Last Name"
          value={data.contact}
          onChange={(e) => handleInputChange('contact', e.target.value)}
          required 
        />
      </label>

      <label className="grid gap-1">
        <span className="label">Email Address <span style={{color: '#ef4444'}}>*</span></span>
        <input 
          className={`input ${validationErrors.email ? 'border-red-500' : ''}`}
          type="email"
          value={data.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          required 
        />
        {validationErrors.email && typeof validationErrors.email === 'string' && (
          <span className="text-xs text-red-500">{validationErrors.email}</span>
        )}
      </label>

      <label className="grid gap-1">
        <span className="label">Mobile Number <span style={{color: '#ef4444'}}>*</span></span>
        <input 
          className={`input ${validationErrors.mobile ? 'border-red-500' : ''}`}
          type="tel"
          value={data.mobile}
          onChange={(e) => handleInputChange('mobile', e.target.value)}
          required 
        />
      </label>

      <div className="md:col-span-2">
        <p className="note mb-2">Please ensure that the email address and mobile number are correct to receive the results by email.</p>
        <p className="note mb-4" style={{color: '#ef4444', fontWeight: 600}}>
          <span style={{color: '#ef4444'}}>*</span> Indicates mandatory input fields that must be completed to proceed.
        </p>
        <div className="flex justify-between">
          <button className="btn flex items-center gap-2" onClick={savePartialData}>
            <SafeIcon icon={FiSave} />
            Save Progress
            {saveNotification && (
              <span className="text-xs bg-white text-green-600 px-2 py-1 rounded-full">
                Saved!
              </span>
            )}
          </button>
          <button className="btn btn-primary" onClick={validateAndContinue}>
            Save Project Information and Continue to Part II →
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProjectInformation;